<template>
    <div class="LoopMetricsDocked q-pa-xs">
        <div class="row items-center q-mt-xs q-mb-sm">
            <div class="text-primary">
                {{ $t('visualization.loopMetrics.title') }}
            </div>
        </div>
        <div class="row q-col-gutter-sm">
            <div class="col-12 col-md-3">
                <div class="KpiLabel">{{ $t('visualization.loopMetrics.avgTime') }}</div>
                <q-card flat class="bg-grey-2">
                    <QLinearProgress
                        size="30px"
                        :value="loopRatio"
                        color="orange-7"
                        trackColor="transparent"
                    >
                        <div class="absolute-full flex flex-center">
                            <QBadge color="white" textColor="primary" class="LoopProgressBadge">
                                <span class="text-weight-bold q-mr-xs">{{ avgLoopTimeFormatted }}</span>
                                <span class="text-secondary">
                                    {{ loopPctOfCase !== null ? `${loopPctOfCase}%` : '0%' }}
                                </span>
                                <QTooltip class="bg-gray-bg text-primary">
                                    {{ $t('visualization.loopMetrics.percentTime') }}
                                </QTooltip>
                            </QBadge>
                        </div>
                    </QLinearProgress>
                </q-card>
            </div>

            <div class="col-12 col-md-3">
                <div class="KpiLabel">{{ $t('visualization.loopMetrics.avgCost') }}</div>
                <q-card flat class="bg-grey-2">
                    <div class="q-pa-xs text-center text-weight-bold text-secondary">
                        {{ avgCostFormatted }}
                    </div>
                </q-card>
            </div>

            <div class="col-12 col-md-3">
                <div class="KpiLabel">{{ $t('visualization.loopMetrics.estTotalEffort') }}</div>
                <q-card flat class="bg-grey-2">
                    <div class="q-pa-xs text-center text-weight-bold">
                        {{ estimatedTotalEffortTimeFormatted }}
                        <span class="q-ml-sm text-secondary">{{ estimatedTotalEffortCostFormatted }}</span>
                    </div>
                </q-card>
            </div>

            <div class="col-12 col-md-3">
                <div class="KpiLabel">{{ $t('visualization.loopMetrics.afterRemoval') }}</div>
                <q-card flat class="bg-grey-2">
                    <div class="q-pa-xs text-center text-weight-bold">
                        <span>{{ estimatedRemovedEffortFormatted }}</span>
                        <span class="q-mx-xs">→</span>
                        <span class="text-secondary">{{ estimatedRemovedCost }}</span>
                    </div>
                </q-card>
            </div>
        </div>

        <q-inner-loading :showing="!hasStats" />
    </div>
</template>

<script>
import VueTypes from 'vue-types'
import moment from 'moment'
import 'moment-duration-format'

export default {
    name: 'LoopMetricsDocked',
    inject: ['App'],
    props: {
        displayedInfo: VueTypes.object,
        chartInfo: VueTypes.any,
        currency: VueTypes.object,
    },
    computed: {
        hasStats() {
            return !!(this.chartInfo && this.chartInfo.stats)
        },
        currencySymbol() {
            const map = { euro: '€', dollar: '$', pound: '£' }
            return map[this.currency?.value] || ''
        },
        avgLoopTime() {
            return this.chartInfo?.stats?.avgDuration
        },
        avgLoopTimeFormatted() {
            return typeof this.avgLoopTime === 'number'
                ? moment.duration(this.avgLoopTime, 'seconds').format('d[d] h[h]:m[m]', { largest: 2, trim: 'all' })
                : '-'
        },
        avgCost() {
            return this.chartInfo?.stats?.avgCost
        },
        avgCostFormatted() {
            if (typeof this.avgCost !== 'number') return '-'
            const formatted = this.App?.numberLocationFormat ? this.App.numberLocationFormat(Math.round(this.avgCost)) : Math.round(this.avgCost)
            return `${formatted}${this.currencySymbol}`
        },
        caseAvgSeconds() {
            return this.displayedInfo?.duration?.avg
        },
        loopPctOfCase() {
            if (typeof this.avgLoopTime !== 'number' || typeof this.caseAvgSeconds !== 'number' || this.caseAvgSeconds <= 0) return null
            return Math.round((this.avgLoopTime / this.caseAvgSeconds) * 100)
        },
        loopRatio() {
            if (typeof this.avgLoopTime !== 'number' || typeof this.caseAvgSeconds !== 'number' || this.caseAvgSeconds <= 0) return 0
            return Math.max(0, Math.min(1, this.avgLoopTime / this.caseAvgSeconds))
        },
        frequency() {
            return this.chartInfo?.frequency
        },
        loopStats() {
            return this.chartInfo?.stats || {}
        },
        estimatedTotalEffortTimeFormatted() {
            return typeof this.avgLoopTime === 'number' && typeof this.frequency === 'number'
                ? moment.duration(this.avgLoopTime * this.frequency, 'seconds').format('d[d] h[h]:m[m]', { largest: 2, trim: 'all' })
                : '-'
        },
        estimatedTotalEffortCostFormatted() {
            const cost = this.loopStats.totalCost
            if (typeof cost !== 'number') return '-'
            const formatted = this.App?.numberLocationFormat ? this.App.numberLocationFormat(Math.round(cost)) : Math.round(cost)
            return `${formatted}${this.currencySymbol}`
        },
        estimatedRemovedEffortFormatted() {
            const seconds = this.loopStats.estimatedRemovedEffortTime
            return typeof seconds === 'number'
                ? moment.duration(seconds, 'seconds').format('d[d] h[h]:m[m]', { largest: 2, trim: 'all' })
                : '-'
        },
        estimatedRemovedCost() {
            const cost = this.loopStats.estimatedRemovedCost
            if (typeof cost !== 'number') return '-'
            const formatted = this.App?.numberLocationFormat ? this.App.numberLocationFormat(Math.round(cost)) : Math.round(cost)
            return `${formatted}${this.currencySymbol}`
        },
    },
}
</script>

<style scoped lang="scss">
.KpiLabel {
    font-size: 0.75rem;
    color: $primary;
}

.LoopProgressBadge {
    max-width: 100%;
    white-space: nowrap;
}
</style>
